import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import ShieldLogo from '../components/ShieldLogo';
import { Check } from 'lucide-react';
import { trackFunnel } from '../lib/funnel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Personal email domains to reject
const PERSONAL_EMAIL_DOMAINS = [
  'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 
  'icloud.com', 'me.com', 'mac.com', 'protonmail.com', 
  'proton.me', 'aol.com', 'live.com', 'msn.com', 'ymail.com'
];

const Wizard = () => {
  const navigate = useNavigate();
  const [showWelcome, setShowWelcome] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [contactInfo, setContactInfo] = useState({ name: '', email: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');

  const sections = [
    {
      title: 'About your business',
      questions: [
        { id: 'q1', label: 'What is your company name?', type: 'text', placeholder: 'e.g. Acme Inc.' },
        { id: 'q2', label: 'What does your product do?', type: 'text' },
        { 
          id: 'q3', 
          label: 'How many people are on your team?', 
          type: 'select',
          options: ['Just me', '2-5', '6-15', '16+']
        },
        { 
          id: 'q4', 
          label: 'Where are your customers based?', 
          type: 'multiselect',
          options: ['US', 'EU', 'Both', 'Other']
        },
        { 
          id: 'q5', 
          label: 'Are you B2B or B2C?', 
          type: 'radio',
          options: ['B2B', 'B2C', 'Both']
        }
      ]
    },
    {
      title: 'Your data',
      questions: [
        { 
          id: 'q9', 
          label: 'Where is your data hosted?', 
          type: 'select',
          options: ['AWS', 'Google Cloud', 'Azure', 'Multiple cloud providers', 'Other', 'Not sure']
        },
        { 
          id: 'q7', 
          label: 'Do you store payment information?', 
          type: 'radio',
          options: ['Yes', 'No', 'We use Paddle or similar']
        },
        { 
          id: 'q8', 
          label: 'Do you store any sensitive data, health, financial or legal?', 
          type: 'radio',
          options: ['Yes', 'No', 'Not sure']
        },
        { 
          id: 'q6', 
          label: 'Do you store customer personal data like name, email, address?', 
          type: 'radio',
          options: ['Yes', 'No', 'Not sure']
        }
      ]
    },
    {
      title: 'Your access controls',
      questions: [
        { 
          id: 'q10', 
          label: 'Do you use single sign-on (SSO)?', 
          type: 'radio',
          options: ['Yes', 'No', 'Not sure']
        },
        { 
          id: 'q11', 
          label: 'Do you have two-factor authentication enabled?', 
          type: 'radio',
          options: ['Yes', 'No', 'Partially']
        },
        { 
          id: 'q12', 
          label: 'Who has access to your production database?', 
          type: 'select',
          options: ['Just me', '2-3 people', '4+ people', 'Not sure']
        },
        { 
          id: 'q13', 
          label: 'Do you use a password manager across your team?', 
          type: 'radio',
          options: ['Yes', 'No', 'Some of us do']
        }
      ]
    },
    {
      title: 'Your current security',
      questions: [
        { 
          id: 'q14', 
          label: 'Do you have a privacy policy live on your website?', 
          type: 'radio',
          options: ['Yes', 'No', 'In progress']
        },
        { 
          id: 'q15', 
          label: 'Do you have any existing security documentation?', 
          type: 'radio',
          options: ['Yes', 'No', 'Some']
        },
        { 
          id: 'q16', 
          label: 'Have you ever had a data breach or security incident?', 
          type: 'radio',
          options: ['No', 'Yes', 'Not sure']
        },
        { 
          id: 'q17', 
          label: 'Do you do any employee security training?', 
          type: 'radio',
          options: ['Yes', 'No', 'Informally']
        }
      ]
    },
    {
      title: 'Your compliance goals',
      questions: [
        { 
          id: 'q18', 
          label: 'Has a prospect ever asked if you are SOC2 compliant?', 
          type: 'radio',
          options: ['Yes', 'No', 'Not yet but expecting it']
        },
        { 
          id: 'q21', 
          label: 'What is your timeline?', 
          type: 'select',
          options: ['ASAP', 'Within 1 month', 'Within 3 months', 'Just exploring']
        },
        { 
          id: 'q19', 
          label: 'Has a prospect ever asked about GDPR compliance?', 
          type: 'radio',
          options: ['Yes', 'No', 'Not yet but expecting it']
        },
        { 
          id: 'q20', 
          label: 'What is your biggest compliance concern right now?', 
          type: 'text' 
        }
      ]
    }
  ];

  const totalSteps = sections.length + 1; // 5 sections + email collection
  const progress = ((currentStep + 1) / totalSteps) * 100;

  const isPersonalEmail = (email) => {
    if (!email) return false;
    const domain = email.toLowerCase().split('@')[1];
    return PERSONAL_EMAIL_DOMAINS.includes(domain);
  };

  const validateWorkEmail = (email) => {
    if (!email) return true; // Don't show error for empty (handled by required)
    if (isPersonalEmail(email)) {
      setEmailError('Please use your work email address.');
      return false;
    }
    setEmailError('');
    return true;
  };

  const handleAnswer = (questionId, value) => {
    setAnswers({ ...answers, [questionId]: value });
  };

  const handleMultiSelect = (questionId, option) => {
    const current = answers[questionId] || [];
    const updated = current.includes(option)
      ? current.filter(item => item !== option)
      : [...current, option];
    setAnswers({ ...answers, [questionId]: updated });
  };

  const canProceed = () => {
    if (currentStep < sections.length) {
      const currentSection = sections[currentStep];
      return currentSection.questions.every(q => {
        const answer = answers[q.id];
        if (q.type === 'multiselect') {
          return answer && answer.length > 0;
        }
        return answer && answer.trim() !== '';
      });
    }
    // Email collection step - check for valid work email
    return contactInfo.name.trim() !== '' && 
           contactInfo.email.trim() !== '' && 
           !isPersonalEmail(contactInfo.email) &&
           !emailError;
  };

  // Track funnel events. wizard_started fires when user clicks the welcome CTA
  // (not on mount). Steps 2..6 fire when the user reaches each subsequent step.
  useEffect(() => {
    // currentStep 0 == step 1 (already tracked by wizard_started on welcome CTA).
    if (!showWelcome && currentStep >= 1 && currentStep <= 5) {
      trackFunnel(`wizard_step_${currentStep + 1}`);
    }
  }, [currentStep, showWelcome]);

  const handleStartAssessment = () => {
    trackFunnel('wizard_started');
    setShowWelcome(false);
  };

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError('');
    trackFunnel('wizard_submitted');

    try {
      const response = await axios.post(`${API}/wizard/submit`, {
        name: contactInfo.name,
        email: contactInfo.email,
        answers: answers
      });

      if (response.data.success) {
        navigate('/wizard/results', {
          state: {
            name: contactInfo.name,
            email: contactInfo.email,
            result_id: response.data.result_id,
            trust_id: response.data.trust_id
          }
        });
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
      setIsSubmitting(false);
    }
  };

  const renderQuestion = (question) => {
    const value = answers[question.id] || '';

    switch (question.type) {
      case 'text':
        return (
          <Input
            type="text"
            value={value}
            onChange={(e) => handleAnswer(question.id, e.target.value)}
            className="bg-white border-[#e0ddd8] mt-3 h-11"
            placeholder={question.placeholder || "Type your answer..."}
            data-testid={`wizard-${question.id}-input`}
          />
        );

      case 'select':
        return (
          <Select value={value} onValueChange={(val) => handleAnswer(question.id, val)} modal={false}>
            <SelectTrigger
              className="bg-white border-[#e0ddd8] mt-3 h-11 touch-manipulation"
              data-testid={`wizard-${question.id}-select-trigger`}
            >
              <SelectValue placeholder="Select an option" />
            </SelectTrigger>
            <SelectContent className="pointer-events-auto">
              {question.options.map((option) => (
                <SelectItem key={option} value={option} data-testid={`wizard-${question.id}-option-${option}`}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case 'radio':
        return (
          <RadioGroup value={value} onValueChange={(val) => handleAnswer(question.id, val)} className="mt-3 space-y-3">
            {question.options.map((option) => (
              <div key={option} className="flex items-center space-x-3">
                <RadioGroupItem
                  value={option}
                  id={`${question.id}-${option}`}
                  className="border-[#e0ddd8]"
                  data-testid={`wizard-${question.id}-radio-${option}`}
                />
                <Label htmlFor={`${question.id}-${option}`} className="cursor-pointer font-normal">
                  {option}
                </Label>
              </div>
            ))}
          </RadioGroup>
        );

      case 'multiselect':
        const selectedOptions = answers[question.id] || [];
        return (
          <div className="mt-3 space-y-3">
            {question.options.map((option) => (
              <div key={option} className="flex items-center space-x-3">
                <Checkbox
                  id={`${question.id}-${option}`}
                  checked={selectedOptions.includes(option)}
                  onCheckedChange={() => handleMultiSelect(question.id, option)}
                  className="border-[#e0ddd8]"
                  data-testid={`wizard-${question.id}-checkbox-${option}`}
                />
                <Label htmlFor={`${question.id}-${option}`} className="cursor-pointer font-normal">
                  {option}
                </Label>
              </div>
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  if (showWelcome) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <div className="border-b border-[#e0ddd8]">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 sm:py-5">
            <Link
              to="/"
              className="inline-flex items-center gap-2 hover:opacity-70 transition-opacity"
            >
              <ShieldLogo size={22} className="sm:hidden" />
              <ShieldLogo size={24} className="hidden sm:inline-block" />
              <span
                className="text-xl sm:text-2xl font-semibold"
                style={{ fontFamily: 'Instrument Serif, serif' }}
              >
                Verilock
              </span>
            </Link>
          </div>
        </div>

        <div
          data-testid="wizard-welcome-screen"
          className="flex-1 flex items-center justify-center px-4 sm:px-6 py-12 sm:py-16"
        >
          <div className="w-full max-w-xl text-center">
            <div className="inline-flex items-center gap-2 bg-[#0a5c44]/10 border border-[#0a5c44]/20 px-3 py-1.5 rounded-full mb-6">
              <span className="w-1.5 h-1.5 bg-[#0a5c44] rounded-full"></span>
              <span className="text-xs font-medium text-[#0a5c44]">
                Free compliance assessment
              </span>
            </div>

            <h1
              className="text-3xl sm:text-4xl lg:text-5xl font-normal mb-4 leading-tight"
              style={{ fontFamily: 'Instrument Serif, serif' }}
            >
              Find out your compliance score in 5 minutes
            </h1>

            <p className="text-base sm:text-lg text-[#1a1a1a]/70 mb-8 leading-relaxed">
              Answer 20 plain English questions about your business. No security expertise needed.
            </p>

            <ul className="space-y-3 text-left max-w-md mx-auto mb-7">
              {[
                'Your personalised compliance score',
                'A prioritised action plan for your business',
                'A preview of your policy documents',
              ].map((line) => (
                <li key={line} className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-[#0a5c44] shrink-0 mt-0.5" />
                  <span className="text-sm sm:text-base text-[#1a1a1a]/85">{line}</span>
                </li>
              ))}
            </ul>

            <p className="text-sm text-[#1a1a1a]/55 mb-6">
              Free to complete. No credit card required.
            </p>

            <Button
              onClick={handleStartAssessment}
              data-testid="wizard-welcome-start-button"
              className="bg-[#1a1a1a] text-white hover:bg-[#2a2a2a] transition-colors px-8 h-12 text-base w-full sm:w-auto"
            >
              See your compliance score
            </Button>
            <p className="text-sm text-[#1a1a1a]/55 mt-3">Free · No credit card required</p>
            <div className="mt-5">
              <Link
                to="/demo"
                data-testid="wizard-welcome-sample-link"
                className="text-sm font-medium text-[#0a5c44] hover:underline inline-flex items-center gap-1"
              >
                Not sure yet? See a sample report
                <span aria-hidden="true">→</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="border-b border-[#e0ddd8]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 sm:py-5">
          <Link
            to="/"
            className="inline-flex items-center gap-2 hover:opacity-70 transition-opacity"
          >
            <ShieldLogo size={22} className="sm:hidden" />
            <ShieldLogo size={24} className="hidden sm:inline-block" />
            <span
              className="text-xl sm:text-2xl font-semibold"
              style={{ fontFamily: 'Instrument Serif, serif' }}
            >
              Verilock
            </span>
          </Link>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-[#f5f3ef] border-b border-[#e0ddd8]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-[#1a1a1a]/60">
              Step {currentStep + 1} of {totalSteps}
              {currentStep === 0 && <span className="text-[#1a1a1a]/45"> — About 5 minutes</span>}
            </p>
            <p className="text-sm text-[#1a1a1a]/60">{Math.round(progress)}%</p>
          </div>
          <div className="w-full bg-[#e0ddd8] rounded-full h-2">
            <div
              className="bg-[#0a5c44] h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12 pb-[100px] sm:pb-12">
        {currentStep < sections.length ? (
          // Section Questions
          <div>
            <h2 className="text-3xl sm:text-4xl font-normal mb-6 sm:mb-8 leading-tight" style={{ fontFamily: 'Instrument Serif, serif' }}>
              {sections[currentStep].title}
            </h2>
            <div className="space-y-6 sm:space-y-8">
              {sections[currentStep].questions.map((question, idx) => {
                // Calculate correct question number based on all previous questions
                let questionNumber = idx + 1;
                for (let i = 0; i < currentStep; i++) {
                  questionNumber += sections[i].questions.length;
                }
                
                return (
                  <div key={question.id}>
                    <Label className="text-sm sm:text-base font-normal text-[#1a1a1a] leading-snug">
                      {questionNumber}. {question.label}
                    </Label>
                    {renderQuestion(question)}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          // Email Collection Step
          <div>
            <h2 className="text-3xl sm:text-4xl font-normal mb-4 leading-tight" style={{ fontFamily: 'Instrument Serif, serif' }}>
              Almost done. Where should we send your compliance roadmap?
            </h2>
            
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm mb-6">
                {error}
              </div>
            )}

            <div className="space-y-6 mt-8">
              <div>
                <Label htmlFor="name" className="text-base font-normal text-[#1a1a1a]">
                  Full name
                </Label>
                <Input
                  id="name"
                  type="text"
                  value={contactInfo.name}
                  onChange={(e) => setContactInfo({ ...contactInfo, name: e.target.value })}
                  className="bg-white border-[#e0ddd8] mt-2"
                  placeholder="Your name"
                  data-testid="wizard-contact-name-input"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-base font-normal text-[#1a1a1a]">
                  Work email
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={contactInfo.email}
                  onChange={(e) => {
                    const email = e.target.value;
                    setContactInfo({ ...contactInfo, email });
                    validateWorkEmail(email);
                  }}
                  onBlur={(e) => validateWorkEmail(e.target.value)}
                  className={`bg-white mt-2 ${emailError ? 'border-red-500' : 'border-[#e0ddd8]'}`}
                  placeholder="you@company.com"
                  data-testid="wizard-contact-email-input"
                />
                {emailError && (
                  <p className="text-sm text-red-600 mt-2">{emailError}</p>
                )}
              </div>

              <p className="text-sm text-[#1a1a1a]/60">
                No spam. Just your roadmap and policy templates.
              </p>
            </div>
          </div>
        )}

        {/* Navigation
            Mobile: fixed at the bottom of the viewport (always visible above
            the iOS/Android keyboard via safe-area padding).
            Desktop (sm+): returns to inline flow inside the form column. */}
        <div
          className="
            fixed bottom-0 left-0 right-0 z-[1000] bg-white border-t border-[#eee]
            px-5 py-4 pb-[max(16px,env(safe-area-inset-bottom))]
            flex items-center gap-3
            sm:static sm:bg-transparent sm:border-t sm:border-[#e0ddd8]
            sm:px-0 sm:py-0 sm:pt-8 sm:mt-12
            sm:flex sm:items-center sm:justify-between sm:gap-3
          "
        >
          <Button
            onClick={handleBack}
            disabled={currentStep === 0}
            variant="outline"
            className="border-[#e0ddd8] text-[#1a1a1a] hover:bg-[#f5f3ef] h-12 px-5 sm:px-6 shrink-0"
            data-testid="wizard-back-button"
          >
            Back
          </Button>

          {currentStep < totalSteps - 1 ? (
            <Button
              onClick={handleNext}
              disabled={!canProceed()}
              className="bg-[#1a1a1a] text-white hover:bg-[#2a2a2a] transition-colors h-12 px-6 sm:px-8 flex-1 sm:flex-none"
              data-testid="wizard-next-button"
            >
              Next
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={!canProceed() || isSubmitting}
              className="bg-[#1a1a1a] text-white hover:bg-[#2a2a2a] transition-colors h-12 px-6 sm:px-8 flex-1 sm:flex-none"
              data-testid="wizard-submit-button"
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Wizard;
